import { createRouter, createWebHistory } from 'vue-router'
import Home from '../views/Home.vue'

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: {
      title: 'Free Online English Conversation Practice',
      description: 'Practice English conversation online for free with our AI-powered speaking partner. Improve your fluency, pronunciation, and confidence without registration.',
      keywords: 'free online English conversation practice, English speaking practice free, practice English conversation, online English speaking, free English practice'
    }
  },
  {
    path: '/conversation',
    name: 'Conversation',
    component: () => import('../views/Conversation.vue'),
    meta: {
      title: 'Free Online English Conversation Practice | Start Speaking Now',
      description: 'Start your free online English conversation practice with our AI speaking partner. No registration required - begin speaking immediately to improve your skills.',
      keywords: 'free online English conversation practice, English speaking practice, voice conversation practice, practice speaking English, online English tutor'
    }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('../views/About.vue'),
    meta: {
      title: 'About Us - EnglishMate',
      description: 'Learn about EnglishMate - the AI-powered platform helping people around the world improve their English conversational skills.',
      keywords: 'about englishmate, english learning platform, AI language learning, english conversation tool'
    }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('../views/Privacy.vue'),
    meta: {
      title: 'Privacy Policy - EnglishMate',
      description: 'Our privacy policy details how we collect, use, and protect your information when using EnglishMate.',
      keywords: 'privacy policy, data protection, englishmate privacy, user privacy'
    }
  },
  {
    path: '/terms',
    name: 'Terms',
    component: () => import('../views/Terms.vue'),
    meta: {
      title: 'Terms of Service - EnglishMate',
      description: 'Read our terms of service to understand the rules and guidelines for using EnglishMate.',
      keywords: 'terms of service, user agreement, terms and conditions, englishmate terms'
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('../views/Contact.vue'),
    meta: {
      title: 'Contact Us - Free Online English Conversation Practice',
      description: 'Get in touch with the EnglishMate team. Ask questions or provide feedback about our free online English conversation practice platform.',
      keywords: 'contact EnglishMate, English practice support, free online English conversation help, English learning contact'
    }
  },
  {
    path: '/disclaimer',
    name: 'Disclaimer',
    component: () => import('../views/Disclaimer.vue'),
    meta: {
      title: 'Data Privacy Disclaimer - We Don\'t Store Your Data',
      description: 'EnglishMate does not store any user data, voice recordings, or conversation history. Learn about our no-data-storage policy and commitment to your privacy.',
      keywords: 'data privacy, no data storage, privacy policy, data protection, privacy disclaimer, voice recording privacy'
    }
  },
  {
    path: '/sitemap',
    name: 'Sitemap',
    component: () => import('../views/Sitemap.vue'),
    meta: {
      title: 'Sitemap - EnglishMate',
      description: 'Navigate through all pages on EnglishMate - the free online English conversation practice platform.',
      keywords: 'sitemap, englishmate pages, site navigation, english practice website'
    }
  },
  {
    path: '/learn-english/vocabulary-playground',
    name: 'VocabularyPlayground',
    component: () => import('../views/VocabularyPlayground.vue'),
    meta: {
      title: 'Interactive Vocabulary Playground: Learn English Words with Audio & Examples',
      description: 'Learn English vocabulary with interactive word cards, native pronunciation, and real-life examples. 1000+ words for Bengali speakers & students.',
      keywords: 'learn English vocabulary, interactive vocabulary learning, vocabulary with audio, pronunciation practice, Bengali learners English, English vocabulary games, spaced repetition, story-based learning, Class 9-11 English, vocabulary playground, English word cards, alternative sentences, vocabulary examples, English learning for Bengali speakers'
    }
  },
  // Safe redirects for potential alternate URLs
  {
    path: '/vocabulary-playground',
    redirect: '/learn-english/vocabulary-playground'
  },
  {
    path: '/vocabulary',
    redirect: '/learn-english/vocabulary-playground'
  },
  {
    path: '/learn-english/vocabulary-playground/active',
    name: 'VocabularyPlaygroundActive',
    component: () => import('../views/VocabularyPlaygroundActive.vue'),
    meta: {
      title: 'Active Vocabulary Learning Session | EnglishMate',
      description: 'Active vocabulary learning session with interactive word cards, pronunciation practice, and example sentences. Focus mode for effective English learning.',
      keywords: 'vocabulary learning session, interactive word cards, English pronunciation practice, vocabulary focus mode, English learning'
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Simple, compatible meta tag management
router.beforeEach((to, from, next) => {
  // Update document title
  document.title = to.meta?.title || 'Free Online English Conversation Practice | EnglishMate'
  
  // Update meta description
  const metaDescription = document.querySelector('meta[name="description"]')
  if (metaDescription) {
    metaDescription.setAttribute('content', to.meta?.description || 'Practice English conversation online for free with our AI-powered speaking partner. Improve your fluency, pronunciation, and confidence without registration.')
  }
  
  // Update meta keywords
  let metaKeywords = document.querySelector('meta[name="keywords"]')
  if (metaKeywords) {
    metaKeywords.setAttribute('content', to.meta?.keywords || 'free online English conversation practice, English speaking practice free, practice English conversation, online English speaking')
  }
  
  // Update canonical URL
  const canonicalLink = document.querySelector('link[rel="canonical"]')
  if (canonicalLink) {
    const baseUrl = 'https://englishmate.io'
    let path = to.path === '/' ? '' : to.path
    if (path.endsWith('/') && path !== '/') {
      path = path.slice(0, -1)
    }
    canonicalLink.href = `${baseUrl}${path}`
  }
  
  // Update Open Graph title
  const ogTitle = document.querySelector('meta[property="og:title"]')
  if (ogTitle) {
    ogTitle.setAttribute('content', to.meta?.title || 'Free Online English Conversation Practice | EnglishMate')
  }
  
  // Update Open Graph description
  const ogDescription = document.querySelector('meta[property="og:description"]')
  if (ogDescription) {
    ogDescription.setAttribute('content', to.meta?.description || 'Practice English conversation online for free with our AI speaking partner. No registration required, available 24/7 for students and learners.')
  }
  
  // Update Open Graph URL
  const ogUrl = document.querySelector('meta[property="og:url"]')
  if (ogUrl) {
    const baseUrl = 'https://englishmate.io'
    let path = to.path === '/' ? '' : to.path
    if (path.endsWith('/') && path !== '/') {
      path = path.slice(0, -1)
    }
    ogUrl.setAttribute('content', `${baseUrl}${path}`)
  }
  
  next()
})

export default router 